/**
 * A fila de curadoria do catálogo.
 *
 * Existe por causa de um número: dos 778 exercícios importados do wger, 33 têm
 * português. Os outros 745 estão em rascunho e não há caminho para publicá-los
 * que não seja `psql` — que é o mesmo que não haver caminho.
 *
 * A tela é uma fila, e não um catálogo: o filtro padrão é "rascunho", e o
 * segundo filtro — "sem português" — é o que separa o trabalho a fazer do
 * trabalho já feito.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import type { CurationItem, PublicationStatus } from '@/features/curation/api';
import { PUBLICATION_LOCALE, titleOf } from '@/features/curation/title';
import {
  curationErrorKey,
  useCreateExercise,
  useCurationQueue,
} from '@/features/curation/useCuration';
import { CATEGORIES, LEVELS, MODALITIES } from '@/features/curation/vocabulary';

const PAGE_SIZE = 25;

const STATUSES: ReadonlyArray<PublicationStatus> = ['draft', 'published', 'archived'];

export default function CurationPage() {
  const { t } = useTranslation(['curation', 'common']);

  const [status, setStatus] = useState<PublicationStatus | ''>('draft');
  const [semPortugues, setSemPortugues] = useState(false);
  const [page, setPage] = useState(0);

  const queue = useCurationQueue({
    publication_status: status || undefined,
    missing_locale: semPortugues ? PUBLICATION_LOCALE : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  function trocarFiltro(acao: () => void) {
    // Trocar de filtro sem voltar à primeira página deixaria a pessoa numa
    // página que talvez não exista mais no novo recorte — tela vazia sem
    // explicação.
    setPage(0);
    acao();
  }

  const total = queue.data?.total ?? 0;
  const items = queue.data?.items ?? [];
  const ultimaPagina = (page + 1) * PAGE_SIZE >= total;

  return (
    <div className="px-5 py-6">
      <h1 className="text-3xl">{t('curation:title')}</h1>
      <p className="mt-1 text-(--color-sand-700)">{t('curation:subtitle')}</p>

      <NovoExercicio />

      <hr className="mt-5 border-(--color-sand-200)" />

      <div className="mt-5 flex flex-wrap gap-2">
        {STATUSES.map((opcao) => (
          <button
            key={opcao}
            type="button"
            aria-pressed={status === opcao}
            onClick={() => trocarFiltro(() => setStatus(status === opcao ? '' : opcao))}
            className={[
              'rounded-full border px-4 py-2 text-sm font-medium transition-colors',
              status === opcao
                ? 'border-(--color-amber-500) bg-(--color-amber-50) text-(--color-sand-900)'
                : 'border-(--color-sand-200) bg-(--color-sand-0) text-(--color-sand-700)',
            ].join(' ')}
          >
            {t(`curation:status.${opcao}`)}
          </button>
        ))}
      </div>

      <label className="mt-3 flex items-center gap-2 text-sm text-(--color-sand-700)">
        <input
          type="checkbox"
          checked={semPortugues}
          onChange={() => trocarFiltro(() => setSemPortugues(!semPortugues))}
          className="size-5 rounded border-(--color-sand-300)"
        />
        {t('curation:filter.missingPortuguese')}
      </label>

      {queue.isError ? (
        <div className="mt-5">
          <Alert tone="error" live="assertive">
            {t(curationErrorKey(queue.error))}
          </Alert>
        </div>
      ) : null}

      {queue.isPending ? (
        <p className="mt-6 text-(--color-sand-700)" aria-busy="true">
          {t('common:state.loading')}
        </p>
      ) : null}

      {queue.isSuccess ? (
        <>
          <p className="mt-5 text-sm text-(--color-sand-700)" role="status">
            {t('curation:count', { count: total })}
          </p>

          {items.length === 0 ? (
            <p className="mt-6 text-(--color-sand-700)">{t('curation:empty')}</p>
          ) : (
            <ul className="mt-3 flex flex-col gap-3">
              {items.map((item) => (
                <li key={item.exercise_id}>
                  <QueueCard item={item} />
                </li>
              ))}
            </ul>
          )}

          {total > PAGE_SIZE ? (
            <nav className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
                className="rounded-xl border border-(--color-sand-200) px-4 py-2 text-sm disabled:opacity-40"
              >
                {t('curation:page.previous')}
              </button>
              <span className="text-sm text-(--color-sand-700)">
                {t('curation:page.position', {
                  from: page * PAGE_SIZE + 1,
                  to: Math.min((page + 1) * PAGE_SIZE, total),
                  total,
                })}
              </span>
              <button
                type="button"
                onClick={() => setPage(page + 1)}
                disabled={ultimaPagina}
                className="rounded-xl border border-(--color-sand-200) px-4 py-2 text-sm disabled:opacity-40"
              >
                {t('curation:page.next')}
              </button>
            </nav>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * Criar o exercício que a importação não trouxe.
 *
 * Só título e classificação. Instruções e mídia ficam para o editor, que abre
 * em seguida — um formulário de criação com todos os campos faria a pessoa
 * parar para preencher antes de ver se o exercício ficou como queria.
 *
 * O formulário começa fechado: a tela é uma fila de 778 itens para revisar, e
 * criar é a exceção. Um formulário sempre aberto no topo empurraria a fila
 * para baixo em toda visita.
 */
function NovoExercicio() {
  const { t } = useTranslation(['curation', 'common']);
  const navigate = useNavigate();
  const criar = useCreateExercise();

  const [aberto, setAberto] = useState(false);
  const [title, setTitle] = useState('');
  const [modality, setModality] = useState<string>('strength');
  const [category, setCategory] = useState<string>('full_body');
  const [level, setLevel] = useState<string>('beginner');

  function enviar() {
    criar.mutate(
      // Sem equipamento: é o que o editor corrige em seguida, e escolher aqui
      // seria adivinhar antes de a pessoa ter descrito o exercício.
      { title: title.trim(), modality, category, level, equipment: [] },
      { onSuccess: (item) => navigate(`/curation/${item.exercise_id}`) },
    );
  }

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="mh-button mh-button--secondary mt-4"
      >
        {t('curation:create.open')}
      </button>
    );
  }

  return (
    <section className="mt-4 rounded-2xl border border-(--color-sand-200) p-4">
      <h2 className="text-lg font-semibold">{t('curation:create.title')}</h2>

      <label className="mt-3 block">
        <span className="text-sm font-medium text-(--color-sand-800)">
          {t('curation:field.title')}
        </span>
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={160}
          autoFocus
          className="mt-1 w-full rounded-xl border border-(--color-sand-200) px-3 py-3"
        />
      </label>

      <Escolha
        label={t('curation:field.modality')}
        value={modality}
        onChange={setModality}
        options={MODALITIES}
        prefix="curation:modality"
      />
      <Escolha
        label={t('curation:field.category')}
        value={category}
        onChange={setCategory}
        options={CATEGORIES}
        prefix="curation:category"
      />
      <Escolha
        label={t('curation:field.level')}
        value={level}
        onChange={setLevel}
        options={LEVELS}
        prefix="curation:level"
      />

      {criar.isError ? (
        <div className="mt-3">
          <Alert tone="error" live="assertive">
            {t(curationErrorKey(criar.error))}
          </Alert>
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={title.trim() === '' || criar.isPending}
          onClick={enviar}
          className="mh-button mh-button--primary"
        >
          {t('curation:create.submit')}
        </button>
        <button
          type="button"
          onClick={() => setAberto(false)}
          className="mh-button mh-button--ghost"
        >
          {t('common:action.cancel')}
        </button>
      </div>
    </section>
  );
}

function Escolha({
  label,
  value,
  onChange,
  options,
  prefix,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<string>;
  prefix: string;
}) {
  const { t } = useTranslation(['curation']);

  return (
    <label className="mt-3 block">
      <span className="text-sm font-medium text-(--color-sand-800)">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded-xl border border-(--color-sand-200) bg-(--color-sand-0) px-3 py-3"
      >
        {options.map((opcao) => (
          <option key={opcao} value={opcao}>
            {t(`${prefix}.${opcao}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

function QueueCard({ item }: { item: CurationItem }) {
  const { t } = useTranslation(['curation']);
  const titulo = titleOf(item);

  return (
    <Link
      to={`/curation/${item.exercise_id}`}
      className="block rounded-2xl border border-(--color-sand-200) bg-(--color-sand-0) p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <span className="font-semibold text-(--color-sand-900)">{titulo.text}</span>
        <StatusBadge status={item.publication_status} />
      </div>

      {/* O idioma do que está sendo lido, quando não é o do produto. Sem esta
          marca, um título em espanhol passaria por português mal escrito. */}
      {!titulo.translated && titulo.locale ? (
        <span className="mt-1 block text-xs text-(--color-sand-600)">
          {t('curation:inLanguage', { locale: t(`curation:locale.${titulo.locale}`) })}
        </span>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-(--color-sand-600)">
        <span>{t(`curation:modality.${item.modality}`)}</span>
        <span aria-hidden="true">·</span>
        <span>{t(`curation:category.${item.category}`)}</span>
        {item.media_kind ? (
          <>
            <span aria-hidden="true">·</span>
            <span>{t(`curation:media.${item.media_kind}`)}</span>
          </>
        ) : (
          <>
            <span aria-hidden="true">·</span>
            <span>{t('curation:media.none')}</span>
          </>
        )}
      </div>

      {item.blockers.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1">
          {item.blockers.map((motivo) => (
            <li
              key={motivo}
              className="rounded-full bg-(--color-amber-50) px-2 py-1 text-xs text-(--color-sand-800)"
            >
              {t(`curation:blocker.${motivo}`)}
            </li>
          ))}
        </ul>
      ) : null}
    </Link>
  );
}

export function StatusBadge({ status }: { status: PublicationStatus }) {
  const { t } = useTranslation(['curation']);

  // Cor **e** palavra, nunca cor sozinha (docs/06_DESIGN_SYSTEM.md §2).
  const estilo: Record<PublicationStatus, string> = {
    draft: 'bg-(--color-sand-100) text-(--color-sand-800)',
    published: 'bg-(--color-mint-100) text-(--color-mint-700)',
    archived: 'bg-(--color-sand-200) text-(--color-sand-700)',
  };

  return (
    <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-medium ${estilo[status]}`}>
      {t(`curation:status.${status}`)}
    </span>
  );
}
